import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import FilterRangeSelector from 'ui-component/filter-range/FilterRangeSelector';
import moment from 'moment';
import { Skeleton } from '@mui/material';
import { LuChevronDown } from 'react-icons/lu';
import { FaFilter } from 'react-icons/fa6';

import { FaSpinner } from 'react-icons/fa';
import { getCurrencySymbolV2 } from 'utils/currency';
import { PiMicrosoftExcelLogo } from 'react-icons/pi';
import { MdKeyboardArrowDown } from 'react-icons/md';
import * as XLSX from 'xlsx';
import usePageTitle from 'hooks/usePageTitle';
import useIsMobile from 'utils/isMobileUtils';

const index = () => {
    const [setPageTitle] = usePageTitle('Profit and Loss');
    const statusTypes = useSelector((state) => state.main.statusTypes);
    const selectedCurrency = useSelector((state) => state.main.selectedCurrency);

    const [showFilterDateUI, setShowFilterDateUI] = useState(false);

    const [selectedFilterBy, setSelectedFilterBy] = useState('MONTH');
    const [defaultSelectedStartYear, setDefaultSelectedStartYear] = useState(moment().year());
    const [defaultSelectedEndYear, setDefaultSelectedEndYear] = useState(moment().year());
    const [defaultSelectedStartMonth, setDefaultSelectedStartMonth] = useState(1);
    const [defaultSelectedEndMonth, setDefaultSelectedEndMonth] = useState(moment().month() + 1);

    const [reportData, setReportData] = useState(null);

    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);

    const isMobile = useIsMobile();


    const processReport = (startYear, endYear, startMonth, endMonth, filterBy) => {
        setIsLoading(true);
        const request = {
            filter_by: filterBy,
            start_year: startYear,
            end_year: endYear,
            start_month: startMonth,
            end_month: endMonth
        }
        
        api.generateProfitLossStatementSummaryReport(request).then((res) => {
            setIsLoading(false);
            if (res?.status) {
                setReportData(res.data); 
            }
        }).catch((error) => {
            setIsLoading(false);
            console.error('Error fetching Profit and Loss Statement Report:', error);
        });

    }

    const formatMonth = (date) => date.toLocaleString("en-US", { month: "short", year: "numeric" });

    const formatQuarter = (date) => {
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        return `Q${quarter} ${date.getFullYear()}`;
    };

    const formatYear = (date) => date.getFullYear();
    const dateFilterTextValue = useMemo(() => {
        if (!defaultSelectedStartYear || !defaultSelectedEndYear || !defaultSelectedStartMonth || !defaultSelectedEndMonth) return "";
        const startDate = new Date(defaultSelectedStartYear, defaultSelectedStartMonth, 0);
        const endDate = new Date(defaultSelectedEndYear, defaultSelectedEndMonth, 0);
        switch (selectedFilterBy) {
            case "MONTH":
            return `${formatMonth(startDate) === formatMonth(endDate) ? formatMonth(startDate) : `${formatMonth(startDate)} - ${formatMonth(endDate)}`}`;

            case "QUARTER":
            return `${formatQuarter(startDate)} - ${formatQuarter(endDate)}`;

            case "YEAR":
                const yearValue = defaultSelectedStartYear === defaultSelectedEndYear ? formatYear(startDate) : `${formatYear(startDate)} - ${formatYear(endDate)}`;
            return yearValue;

            default:
            return "";
        }
    }, [selectedFilterBy, defaultSelectedStartYear, defaultSelectedEndYear, defaultSelectedStartMonth, defaultSelectedEndMonth]);
    
    useEffect(() => {
        processReport(moment().year(), defaultSelectedEndYear, '1', defaultSelectedEndMonth, selectedFilterBy);
    }, []);


	const s2ab = (s) => {
		const buf = new ArrayBuffer(s.length);
		const view = new Uint8Array(buf);
		for (let i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
		return buf;
	};

    const exportReportXlsx = () => {
        const request = {
            filter_by: selectedFilterBy,
            start_year: defaultSelectedStartYear,
            end_year: defaultSelectedEndYear,
            start_month: defaultSelectedStartMonth,
            end_month: defaultSelectedEndMonth,
            type: 'excel'
        };

        setIsExporting(true);
        api.generateProfitLossStatementSummaryReport(request).then((res) => {
            setIsExporting(false);
            // 1️⃣ Format API data into Excel table
            const excelData = formatProfitLossForExcel(res?.data);

            // 2️⃣ Create workbook & worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet(excelData);

            // 3️⃣ Append sheet
            XLSX.utils.book_append_sheet(wb, ws, 'Profit & Loss Statement');

            // 4️⃣ Export
            const wbBinaryString = XLSX.write(wb, {
                bookType: 'xlsx',
                type: 'binary'
            });

            const blob = new Blob(
                [s2ab(wbBinaryString)],
                { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
            );

            saveAs(blob, 'Projectmix Profit and Loss Statement.xlsx');
        });
    };

    const formatProfitLossForExcel = (data) => {


        let currecySymbol = '$';
        if (selectedCurrency === 'usd') {
            currecySymbol = '$';
        } else if (selectedCurrency === 'eur') {
            currecySymbol = '€';
        } else if (selectedCurrency === 'aed') {
            currecySymbol = 'AED';
        } else {
            currecySymbol = '$';
        }

        const table = [
            [
                '',
                ...data?.keys?.map(item => selectedFilterBy === "MONTH" ? moment(item, "YYYY-MM").format("MMM YYYY") : item
                )
            ]
        ];
        table.push(['Revenue', ...data?.keys?.map(() => '')]);
        
        table.push([
            'Sales Income',
            ...data?.keys?.map(item => `${currecySymbol} ${(data?.revenue?.sales_income[item]?.[selectedCurrency === 'all' ? 'usd' : selectedCurrency] || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            )
        ]);

        table.push([
            'Total Revenue',
            ...data?.keys?.map(item =>
                `${currecySymbol} ${(data?.revenue?.total_revenue[item]?.[selectedCurrency === 'all' ? 'usd' : selectedCurrency] || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            )
        ]);
        
        
        table.push(['', ...data?.keys?.map(() => '')]);
        
        // ---- EXPENSES SECTION ----
        table.push(['Expenses', ...data?.keys?.map(() => '')]);
        
        table.push([
            'Purchase Order',
            ...data?.keys?.map(item =>
                `${currecySymbol} ${(data?.expenses?.purchase_orders[item]?.[selectedCurrency === 'all' ? 'usd' : selectedCurrency] || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            )
        ]);

        table.push([
            'Shipping Costs',
            ...data?.keys?.map(item =>
                `${currecySymbol} ${(data?.expenses?.shipping_costs[item]?.[selectedCurrency === 'all' ? 'usd' : selectedCurrency] || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            )
        ]);


        table.push([
            'Total Expenses',
            ...data?.keys?.map(item =>
                `${currecySymbol} ${(data?.expenses?.total_expenses[item]?.[selectedCurrency === 'all' ? 'usd' : selectedCurrency] || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            )
        ]);

        table.push(['', ...data?.keys?.map(() => '')]);

        table.push([
            'Net Profit',
            ...data?.keys?.map(item =>
                `${currecySymbol} ${(data?.net_profit?.[item]?.[selectedCurrency === 'all' ? 'usd' : selectedCurrency] || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            )
        ]);

        return table;
    };


    const [showSalesIncomeCard, setShowSalesIncomeCard] = useState(false);
    const [showTotalRevenueCard, setShowTotalRevenueCard] = useState(false);
    const [showPurchaseOrdersCard, setShowPurchaseOrdersCard] = useState(false);
    const [showShippingCostsCard, setShowShippingCostsCard] = useState(false);
    const [showTotalExpensesCard, setShowTotalExpensesCard] = useState(false);
    const [showNetProfitCard, setShowNetProfitCard] = useState(false);

    return (
        <>
            <div className="row justify-content-center m-0">
                <div className="medium-sized-container" style={{ maxWidth: '900px' }}>
                    
                    <div className="col-xl-12 col-lg-12 p-0">
                        <div className="show-on-mobile new-page-header" style={{ position: 'relative' }}>
                            <div className="header-bubble" />
                            <div className="d-flex flex-column gap-2 " style={{ position: 'relative', zIndex: 1, paddingTop: 10, width: '100%' }}>
                                <h5 style={{ textAlign: 'center' }}>Profit and Loss</h5>


                                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                                    <button
                                        className="btn btn-primary btn-outlined-main white-var"
                                        onClick={() => {
                                            if (!isLoading) {
                                                setShowFilterDateUI(!showFilterDateUI)
                                            }
                                        }}
                                    >
                                        <span className="menuIcon">
                                            <FaFilter style={{ fontSize: '1em' }} />
                                        </span>
                                        <span>Filter</span>
                                    </button>
                                    <button
                                        className={`btn btn-primary btn-outlined-main white-var ${isExporting ? 'disabled' : ''}`}
                                        onClick={() => {
                                            if (!isExporting) {
                                                exportReportXlsx();
                                            }
                                        }}
                                        disabled={isExporting}
                                    >
                                        <span className="menuIcon">
                                            <PiMicrosoftExcelLogo style={{ fontSize: '1.4em' }} /> 
                                        </span>
                                        <span>Export</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                        {
                            isMobile && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 10,
                                        paddingTop: 0,
                                        paddingBottom: 5,
                                        paddingLeft: 20,
                                        paddingRight: 20
                                    }}
                                    className="show-on-mobile"
                                >
                                    {
                                        showFilterDateUI && (
                                            <FilterRangeSelector
                                                showFilter={showFilterDateUI}
                                                defaultSelectedStartYear={defaultSelectedStartYear}
                                                defaultSelectedEndYear={defaultSelectedEndYear}
                                                defaultSelectedStartMonth={defaultSelectedStartMonth}
                                                defaultSelectedEndMonth={defaultSelectedEndMonth}
                                                onApplyFilter={(startYear, endYear, startMonth, endMonth, filterBy) => {
                                                    setDefaultSelectedStartYear(startYear);
                                                    setDefaultSelectedEndYear(endYear);
                                                    setDefaultSelectedStartMonth(startMonth);
                                                    setDefaultSelectedEndMonth(endMonth);
                                                    setSelectedFilterBy(filterBy);
                                                    setShowFilterDateUI(false);
                                                    processReport(startYear, endYear, startMonth, endMonth, filterBy);
                                                    // Apply your filtering logic here based on selected dates
                                                }}
                                                showFilterBy={selectedFilterBy}
                                                onFilterVisibility={() => {
                                                    setShowFilterDateUI(false);
                                                }}
                                            />
                                        )
                                    }
                                </div>
                            )
                        }
                        <div className="section-head-new pl-0 hide-on-mobile" style={{ margin: '10px 0' }}>
                            <div>
                                <button
                                    className={`btn btn-primary btn-outlined-main ${isLoading ? 'disabled' : ''}`}
                                    style={{
                                        gap: '0',
                                        zIndex: '12'
                                    }}
                                    onClick={() => {
                                        if (!isLoading) {
                                            setShowFilterDateUI(!showFilterDateUI)
                                        }
                                    }}
                                >
                                    Profit and loss statement for {dateFilterTextValue}
                                    <span className={`button-rotating-arrow ${showFilterDateUI ? 'rotate-180' : ''} `}>
                                        <MdKeyboardArrowDown />
                                    </span>
                                </button>
                                {
                                    showFilterDateUI && (
                                        <FilterRangeSelector
                                            showFilter={showFilterDateUI}
                                            defaultSelectedStartYear={defaultSelectedStartYear}
                                            defaultSelectedEndYear={defaultSelectedEndYear}
                                            defaultSelectedStartMonth={defaultSelectedStartMonth}
                                            defaultSelectedEndMonth={defaultSelectedEndMonth}
                                            onApplyFilter={(startYear, endYear, startMonth, endMonth, filterBy) => {
                                                setDefaultSelectedStartYear(startYear);
                                                setDefaultSelectedEndYear(endYear);
                                                setDefaultSelectedStartMonth(startMonth);
                                                setDefaultSelectedEndMonth(endMonth);
                                                setSelectedFilterBy(filterBy);
                                                setShowFilterDateUI(false);
                                                processReport(startYear, endYear, startMonth, endMonth, filterBy);
                                                // Apply your filtering logic here based on selected dates
                                            }}
                                            showFilterBy={selectedFilterBy}
                                            onFilterVisibility={() => {
                                                setShowFilterDateUI(false);
                                            }}
                                        />
                                    )
                                }
                            </div>
                            <div className="actions">
                                
                                {isLoading ? (
                                    <Dropdown
                                        className="btnDel est-btn iconbtn disabled"
                                        align={'end'}
                                        disabled
                                    >
                                        <Dropdown.Toggle as="span">
                                            <Link
                                                to={''}
                                                className="btn btn-primary btn-outlined-main white-var disabled" disabled
                                                style={{ minWidth: 'unset', gap: '5px' }}
                                            >
                                                <span className="menuIcon">
                                                    <PiMicrosoftExcelLogo style={{ fontSize: '1.4em' }} /> 
                                                </span>
                                                <span>Export</span>
                                            </Link>
                                        </Dropdown.Toggle>
                                    </Dropdown>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (!isExporting) {
                                                exportReportXlsx();
                                            }
                                        }}
                                        className={`btn btn-primary btn-outlined-main ${isExporting ? 'disabled' : ''}`}
                                        disabled={isExporting}
                                    >
                                        {
                                            isExporting ? (
                                                <>
                                                    <span className="menuIcon">
                                                        <FaSpinner className="fa-spin" style={{ fontSize: '1.4em' }} />
                                                    </span>
                                                    <span>Exporting...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="menuIcon">
                                                        <PiMicrosoftExcelLogo style={{ fontSize: '1.4em' }} /> 
                                                    </span>
                                                    <span>Export</span>
                                                </>
                                            )
                                        }
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className={`card shadow-sm  bg-white mb-4 table-card ${isMobile ? 'shorter-table' : ''}`}>
                            
                            <div className="estimation-table-area">
                                <div className="table-side">

                                    {
                                        !isMobile ? (
                                            <>
                                                <div style={{ width: '100%' }}>
                                                    <div className="row">
                                                        <div className="col-3" style={{ padding: '0' }}> 
                                                            <div className="row">
                                                                <div
                                                                    className="col-12"
                                                                    style={{
                                                                        background: '#ebebeb',
                                                                        height: '35px',
                                                                        marginTop: '-10px',
                                                                        borderTopLeftRadius: '10px',
                                                                        marginBottom: '10px',
                                                                        marginLeft: '10px'
                                                                    }}
                                                                >
                                                                </div>
                                                            </div>
                                                            <div className="row" style={{ margin: '0 0 10px 0' }}>
                                                                <div className="col-12" style={{ fontSize: '16px' }}>
                                                                    Revenue
                                                                </div>
                                                            </div>
                                                            <div className="row" style={{ margin: '0px 0 0 25px' }}>
                                                                <div className="col-12" >
                                                                    {
                                                                        isLoading ? (
                                                                            <div style={{ width: '100px', marginTop: '2px' }}>
                                                                                <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                            </div>
                                                                        ) : (
                                                                            <a
                                                                                href={`profit-loss-statement/sales?filterBy=${selectedFilterBy}&startYear=${defaultSelectedStartYear}&startMonth=${defaultSelectedStartMonth}&endYear=${defaultSelectedEndYear}&endMonth=${defaultSelectedEndMonth}`}
                                                                                style={{ textDecoration: 'none' }}
                                                                                target="_blank"
                                                                            >
                                                                                Sales Income
                                                                            </a>
                                                                        )
                                                                    }
                                                                </div>
                                                            </div>
                                                            {/* <hr /> */}
                                                                <div
                                                                    style={{
                                                                        borderBottom: '1px solid #e0e0e0',
                                                                        paddingTop: '13px',
                                                                        background: '#fff',
                                                                        marginBottom: '14px',
                                                                        // position: 'absolute',
                                                                        width: '100%'
                                                                    }}
                                                                ></div>
                                                            <div className="row" style={{ margin: '0' }}>
                                                                <div className="col-12">
                                                                    Total Revenue
                                                                </div>
                                                            </div>
                                                            <div className="row" style={{ margin: '40px 0 10px 0' }}>
                                                                <div className="col-12" style={{ fontSize: '16px' }}>
                                                                    Expenses
                                                                </div>
                                                            </div>
                                                            <div className="row" style={{ margin: '0px 0 0 25px' }}>
                                                                <div className="col-12" >
                                                                    {
                                                                        isLoading ? (
                                                                            <div style={{ width: '100px', marginTop: '2px' }}>
                                                                                <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                            </div>
                                                                        ) : (
                                                                            <a
                                                                                href={`profit-loss-statement/purchase-orders?filterBy=${selectedFilterBy}&startYear=${defaultSelectedStartYear}&startMonth=${defaultSelectedStartMonth}&endYear=${defaultSelectedEndYear}&endMonth=${defaultSelectedEndMonth}`}
                                                                                style={{ textDecoration: 'none' }}
                                                                                target="_blank"
                                                                            >
                                                                                Purchase Orders
                                                                            </a>
                                                                        )
                                                                    }
                                                                </div>
                                                            </div>
                                                            <div className="row" style={{ margin: '20px 0 0 25px' }}>
                                                                <div className="col-12" >
                                                                    {
                                                                        isLoading ? (
                                                                            <div style={{ width: '130px' }}>
                                                                                <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                            </div>
                                                                        ) : (

                                                                            <a
                                                                                href={`profit-loss-statement/shipping-costs?filterBy=${selectedFilterBy}&startYear=${defaultSelectedStartYear}&startMonth=${defaultSelectedStartMonth}&endYear=${defaultSelectedEndYear}&endMonth=${defaultSelectedEndMonth}`}
                                                                                style={{ textDecoration: 'none' }}
                                                                                target="_blank"
                                                                            >
                                                                                Shipping Costs
                                                                            </a>
                                                                        )
                                                                    }
                                                                </div>
                                                            </div>
                                                            {/* <hr /> */}
                                                                <div
                                                                    style={{
                                                                        borderBottom: '1px solid #e0e0e0',
                                                                        paddingTop: '13px',
                                                                        background: '#fff',
                                                                        marginBottom: '14px',
                                                                        // position: 'absolute',
                                                                        width: '100%'
                                                                    }}
                                                                ></div>
                                                            <div className="row" style={{ margin: '0 0 10px 0' }}>
                                                                <div className="col-12">
                                                                    Total Expenses
                                                                </div>
                                                            </div>
                                                            <div
                                                                style={{
                                                                    borderBottom: '1px solid #e0e0e0',
                                                                    paddingTop: '2px',
                                                                    background: '#fff',
                                                                    marginBottom: '0px',
                                                                    position: 'absolute',
                                                                    width: '100%'
                                                                }}
                                                            ></div>
                                                            
                                                            <div className="row" style={{ margin: '40px 0 10px 0' }}>
                                                                <div className="col-12" style={{ fontSize: '16px' }}>
                                                                    Net Profit
                                                                </div>
                                                            </div>
                                                        </div>



                                                        <div
                                                            className="col-9 flex justify-content-end"
                                                            style={{
                                                                background: '#ebebeb',
                                                                lineHeight: '35px',
                                                                marginTop: '-10px',
                                                                borderTopRightRadius: '10px',
                                                                padding: '0',
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    width: '100%',
                                                                    overflowX: 'auto',
                                                                    scrollbarWidth: 'thin',
                                                                    scrollbarColor: '#0966ff transparent',
                                                                    background: '#fff' 
                                                                }}
                                                            >
                                                                <div style={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: `repeat(${!isLoading ? reportData?.keys?.length : 12}, 120px)`,
                                                                    background: '#ebebeb',
                                                                }}>
                                                                    {
                                                                        isLoading ? (
                                                                            [...Array(12)].map((__, i) => (
                                                                                <div key={i} style={{ background: '#ebebeb' }}>
                                                                                    <div style={{ width: '100px', padding: '8.5px 0' }}>
                                                                                        <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            reportData?.keys?.map((month, index) => (
                                                                                <div key={index} style={{ background: '#ebebeb' }}>
                                                                                    {selectedFilterBy === "MONTH"
                                                                                        ? moment(month, "YYYY-MM").format("MMM YYYY") 
                                                                                        : month}
                                                                                </div>
                                                                            ))
                                                                        )
                                                                    }
                                                                </div>
                                                                <div className="row" style={{ margin: '0 0 10px 0' }}>
                                                                    <div className="col-12" style={{ height: '26px' }}></div>
                                                                </div>
                                                                <div style={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: `repeat(${!isLoading ? reportData?.keys?.length : 12}, 120px)`,
                                                                    background: '#fff'
                                                                }}>

                                                                    {
                                                                        isLoading ? (
                                                                            [...Array(12)].map((__, i) => (
                                                                                <div key={i}>
                                                                                    <div style={{ width: '100px', padding: '8.5px 0' }}>
                                                                                        <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            reportData?.keys?.map((month, index) => (
                                                                                <div
                                                                                    key={index}
                                                                                    className="flex"
                                                                                    style={{
                                                                                        position: 'relative', 
                                                                                        gap: '3px'
                                                                                    }}
                                                                                >
                                                                                    <div 
                                                                                        style={{
                                                                                            marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0'}`,
                                                                                            color: reportData?.revenue?.sales_income[month]?.[selectedCurrency] > 0 ? '#000' : '#858796'
                                                                                        }}
                                                                                    >
                                                                                        {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, 'usd', '11px', '11px')}
                                                                                    </div>
                                                                                    <div>
                                                                                        {
                                                                                            reportData?.revenue?.sales_income[month]?.[selectedCurrency] > 0 ? (
                                                                                                `${reportData?.revenue?.sales_income[month]?.[selectedCurrency]?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00'}`
                                                                                            ): (
                                                                                                <span class="text-gray-600">0.00</span>     
                                                                                            )
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        )
                                                                    }
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        borderBottom: '1px solid #e0e0e0',
                                                                        paddingTop: '6px',
                                                                        background: '#fff',
                                                                        marginBottom: '0px',
                                                                        position: 'absolute',
                                                                        width: '100%'
                                                                    }}
                                                                ></div>
                                                                <div style={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: `repeat(${!isLoading ? reportData?.keys?.length : 12}, 120px)`,
                                                                    background: '#fff',
                                                                    paddingTop: '12px'
                                                                }}>
                                                                    {
                                                                        isLoading ? (
                                                                            [...Array(12)].map((__, i) => (
                                                                                <div key={i}>
                                                                                    <div style={{ width: '100px', padding: '8.5px 0' }}>
                                                                                        <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            reportData?.keys?.map((month, index) => (
                                                                                <div
                                                                                    key={index}
                                                                                    className="flex"
                                                                                    style={{
                                                                                        position: 'relative', 
                                                                                        gap: '3px'
                                                                                    }}
                                                                                >

                                                                                    <div 
                                                                                        style={{
                                                                                            marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0'}`,
                                                                                            color: reportData?.revenue?.total_revenue[month]?.[selectedCurrency] > 0 ? '#000' : '#858796'
                                                                                        }}
                                                                                    >
                                                                                        {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, 'usd', '11px', '11px')}
                                                                                    </div>
                                                                                    <div>
                                                                                        {
                                                                                            reportData?.revenue?.total_revenue[month]?.[selectedCurrency] > 0 ? (
                                                                                                `${reportData?.revenue?.total_revenue[month]?.[selectedCurrency]?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00'}`
                                                                                            ): (
                                                                                                <span class="text-gray-600">0.00</span>     
                                                                                            )
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        )
                                                                    }
                                                                </div>

                                                                <div className="row" style={{ margin: '40px 0 10px 0' }}>
                                                                    <div className="col-12" style={{ height: '4px' }}></div>
                                                                </div>

                                                                <div style={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: `repeat(${!isLoading ? reportData?.keys?.length : 12}, 120px)`,
                                                                    background: '#fff',
                                                                    paddingTop: '5px'
                                                                }}>
                                                                    {
                                                                        isLoading ? (
                                                                            [...Array(12)].map((__, i) => (
                                                                                <div key={i}>
                                                                                    <div style={{ width: '100px', padding: '8.5px 0' }}>
                                                                                        <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            reportData?.keys?.map((month, index) => (
                                                                                <div
                                                                                    key={index}
                                                                                    className="flex"
                                                                                    style={{
                                                                                        position: 'relative', 
                                                                                        gap: '3px'
                                                                                    }}
                                                                                >
                                                                                    <div 
                                                                                        style={{
                                                                                            marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0'}`,
                                                                                            color: reportData?.expenses?.purchase_orders[month]?.[selectedCurrency] > 0 ? '#000' : '#858796'
                                                                                        }}
                                                                                    >
                                                                                        {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, 'usd', '11px', '11px')}
                                                                                    </div>
                                                                                    <div>
                                                                                        {
                                                                                            reportData?.expenses?.purchase_orders[month]?.[selectedCurrency] > 0 ? (
                                                                                                `${reportData?.expenses?.purchase_orders[month]?.[selectedCurrency]?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00'}`
                                                                                            ): (
                                                                                                <span class="text-gray-600">0.00</span>     
                                                                                            )
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        )
                                                                    }
                                                                </div>
                                                                <div style={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: `repeat(${!isLoading ? reportData?.keys?.length : 12}, 120px)`,
                                                                    background: '#fff',
                                                                    paddingTop: '5px'
                                                                }}>
                                                                    {
                                                                        isLoading ? (
                                                                            [...Array(12)].map((__, i) => (
                                                                                <div key={i}>
                                                                                    <div style={{ width: '100px', padding: '8.5px 0' }}>
                                                                                        <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            reportData?.keys?.map((month, index) => (
                                                                                <div
                                                                                    key={index}
                                                                                    className="flex"
                                                                                    style={{
                                                                                        position: 'relative', 
                                                                                        gap: '3px'
                                                                                    }}
                                                                                >
                                                                                    <div 
                                                                                        style={{
                                                                                            marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0'}`,
                                                                                            color: reportData?.expenses?.shipping_costs[month]?.[selectedCurrency] > 0 ? '#000' : '#858796'
                                                                                        }}
                                                                                    >
                                                                                        {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, 'usd', '11px', '11px')}
                                                                                    </div>
                                                                                    <div>
                                                                                        {
                                                                                            reportData?.expenses?.shipping_costs[month]?.[selectedCurrency] > 0 ? (
                                                                                                `${reportData?.expenses?.shipping_costs[month]?.[selectedCurrency]?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00'}`
                                                                                            ): (
                                                                                                <span class="text-gray-600">0.00</span>     
                                                                                            )
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                            ))
     
                                                                        )
                                                                    }

                                                                </div>

                                                                <div
                                                                    style={{
                                                                        borderBottom: '1px solid #e0e0e0',
                                                                        paddingTop: '5px',
                                                                        background: '#fff',
                                                                        marginBottom: '14px',
                                                                        position: 'absolute',
                                                                        width: '100%'
                                                                    }}
                                                                ></div>
                                                                <div style={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: `repeat(${!isLoading ? reportData?.keys?.length : 12}, 120px)`,
                                                                    background: '#fff',
                                                                    paddingTop: '12px'
                                                                }}>

                                                                    {
                                                                        isLoading ? (
                                                                            [...Array(12)].map((__, i) => (
                                                                                <div key={i} className="row">
                                                                                    <div className="col-12">
                                                                                        <div style={{ width: '100px', padding: '8.5px 0' }}>
                                                                                            <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        ) : (
     
                                                                            reportData?.keys?.map((month, index) => (
                                                                                <div key={index} className="row">
                                                                                    <div className="col-12">
                                                                                        <div
                                                                                            key={index}
                                                                                            className="flex"
                                                                                            style={{
                                                                                                position: 'relative', 
                                                                                                gap: '3px'
                                                                                            }}
                                                                                        >
                                                                                            <div 
                                                                                                style={{
                                                                                                    marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0'}`,
                                                                                                    color: reportData?.expenses?.total_expenses[month]?.[selectedCurrency] > 0 ? '#000' : '#858796'
                                                                                                }}
                                                                                            >
                                                                                                {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, 'usd', '11px', '11px')}
                                                                                            </div>
                                                                                            <div>
                                                                                                {
                                                                                                    reportData?.expenses?.total_expenses[month]?.[selectedCurrency] > 0 ? (
                                                                                                        `${reportData?.expenses?.total_expenses[month]?.[selectedCurrency]?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00'}`
                                                                                                    ): (
                                                                                                        <span class="text-gray-600">0.00</span>     
                                                                                                    )
                                                                                                }
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        )
                                                                    }
                                                                </div>



                                                                <div
                                                                    style={{
                                                                        borderBottom: '1px solid #e0e0e0',
                                                                        paddingTop: '5px',
                                                                        background: '#fff',
                                                                        marginBottom: '14px',
                                                                        position: 'absolute',
                                                                        width: '100%'
                                                                    }}
                                                                ></div>


                                                                <div className="row" style={{ margin: '5px 0 10px 0' }}>
                                                                    <div className="col-12"></div>
                                                                </div>

                                                                <div style={{
                                                                    display: 'grid',
                                                                    // gridTemplateColumns: 'repeat(12, 120px)',
                                                                    gridTemplateColumns: `repeat(${!isLoading ? reportData?.keys?.length : 12}, 120px)`,
                                                                    background: '#fff',
                                                                    paddingTop: '12px'
                                                                }}>
                                                                    {
                                                                        isLoading ? (
                                                                            [...Array(12)].map((__, i) => (
                                                                                <div key={i} className="row">
                                                                                    <div className="col-12">
                                                                                        <div style={{ width: '100px', padding: '8.5px 0' }}>
                                                                                            <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            reportData?.keys?.map((month, index) => (
                                                                                <div key={index} className="row">
                                                                                    <div className="col-12">
                                                                                        <div
                                                                                            className="flex"
                                                                                            style={{
                                                                                                position: 'relative', 
                                                                                                gap: '3px'
                                                                                            }}
                                                                                        >
                                                                                            <div 
                                                                                                style={{
                                                                                                    marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0'}`,
                                                                                                    color: reportData?.net_profit[month]?.[selectedCurrency] > 0 ? '#000' : '#858796'
                                                                                                }}
                                                                                            >
                                                                                                {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, 'usd', '11px', '11px')}
                                                                                            </div>
                                                                                            <div>
                                                                                                {
                                                                                                    reportData?.net_profit[month]?.[selectedCurrency] > 0 ? (
                                                                                                        `${reportData?.net_profit[month]?.[selectedCurrency]?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00'}`
                                                                                                    ): (
                                                                                                        <span class="text-gray-600">0.00</span>     
                                                                                                    )
                                                                                                }
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ))      
                                                                        )
                                                                    }
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                            </>
                                        ) : (
                                            <>
                                                 
                                                <div className="inv-cards-list d-md-none" style={{ padding: '0 15px 15px 15px' }}>
                                                    <div
                                                        className="order-list-card"
                                                        style={{
                                                            background: 'rgb(249, 249, 249)',
                                                            padding: '10px 0',
                                                            display: 'flex',
                                                            border: '0',
                                                            fontSize: '16px',
                                                        }}
                                                    >
                                                        Revenue
                                                    </div>    



                                                    <div
                                                        className="order-list-card"
                                                        style={{
                                                            background: '#fff',
                                                            // marginBottom: 10,
                                                            borderTopLeftRadius: '12px',
                                                            borderTopRightRadius: '12px',
                                                            borderBottomRightRadius: showSalesIncomeCard ? '0px' : '12px',
                                                            borderBottomLeftRadius: showSalesIncomeCard ? '0px' : '12px',
                                                            padding: '10px 20px',
                                                            display: 'flex',
                                                            flexDirection: 'initial',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            gap: 10, 
                                                            border: '1px solid #dfdfdf'
                                                        }}
                                                        onClick={() => {
                                                            if (!isLoading) {
                                                                setShowSalesIncomeCard(!showSalesIncomeCard)
                                                            }
                                                        }}
                                                    >
                                                        <div>
                                                            Sales Income
                                                        </div>
                                                        <div>
                                                            
                                                            <div
                                                                className={`invoice-attachment-collapse-icon ${showSalesIncomeCard ? 'collapsed' : ''}`}
                                                                style={{
                                                                    height: '17px', 
                                                                    fontSize: '25px', 
                                                                    background: '#fff'
                                                                }}
                                                            >
                                                                {/* {collapsedAttachments[index] ? <LuChevronDown /> : <LuChevronUp />} */}
                                                                <LuChevronDown />
                                                            </div>
                                                        </div>
                                                        
                                                    </div> 
                                                    {
                                                        showSalesIncomeCard && (
                                                            <div
                                                                className="order-list-card"
                                                                style={{
                                                                    background: '#fff',
                                                                    padding: 16,
                                                                    gap: 10, 
                                                                    border: '1px solid #dfdfdf',
                                                                    borderBottomLeftRadius: '12px',
                                                                    borderBottomRightRadius: '12px',
                                                                }}
                                                            >
                                                                {
                                                                    isLoading ? (
                                                                        [...Array(5)].map((__, i) => (
                                                                            <div className="flex align-items-center justify-between mb-1" key={i}>

                                                                                <div style={{ width: '100px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                </div>
                                                                                <div style={{ width: '100px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        reportData?.keys?.map((month, index) => (

                                                                            <div className="flex align-items-center justify-between  mb-1" key={index}>

                                                                                <div className="text-gray-800" style={{ minWidth: '110px' }}>
                                                                                    {selectedFilterBy === "MONTH"
                                                                                        ? moment(month, "YYYY-MM").format("MMMM YYYY") 
                                                                                        : month}
                                                                                </div>
                                                                                <div>

                                                                                    {reportData?.revenue?.sales_income[month]?.usd !== undefined
                                                                                        ? `$ ${reportData.revenue.sales_income[month].usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                                                        : '<span class="text-gray-600">$ 0.00</span>'}
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    )
                                                                }
                                                            </div>
                                                        )
                                                    }
                                                    

                                                    <div
                                                        className="order-list-card"
                                                        style={{
                                                            background: '#fff',
                                                            marginTop: '15px',
                                                            borderTopLeftRadius: '12px',
                                                            borderTopRightRadius: '12px',
                                                            borderBottomRightRadius: showTotalRevenueCard ? '0px' : '12px',
                                                            borderBottomLeftRadius: showTotalRevenueCard ? '0px' : '12px',
                                                            padding: '10px 20px',
                                                            display: 'flex',
                                                            flexDirection: 'initial',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            gap: 10, 
                                                            border: '1px solid #dfdfdf'
                                                        }}
                                                        onClick={() => {
                                                            if (!isLoading) {
                                                                setShowTotalRevenueCard(!showTotalRevenueCard)
                                                            }
                                                        }}
                                                    >
                                                        <div>
                                                            Total Revenue
                                                        </div>
                                                        <div>
                                                            
                                                            <div
                                                                className={`invoice-attachment-collapse-icon ${showTotalRevenueCard ? 'collapsed' : ''}`}
                                                                style={{
                                                                    height: '17px', 
                                                                    fontSize: '25px', 
                                                                    background: '#fff'
                                                                }}
                                                            >
                                                                <LuChevronDown />
                                                            </div>
                                                        </div>
                                                        
                                                    </div> 
                                                    
                                                    {
                                                        showTotalRevenueCard && (
                                                            <div
                                                                className="order-list-card"
                                                                style={{
                                                                    background: '#fff',
                                                                    padding: 16,
                                                                    gap: 10, 
                                                                    border: '1px solid #dfdfdf',
                                                                    borderBottomLeftRadius: '12px',
                                                                    borderBottomRightRadius: '12px',
                                                                }}
                                                            >
                                                                {
                                                                    isLoading ? (
                                                                        [...Array(5)].map((__, i) => (
                                                                            <div className="flex align-items-center justify-between mb-1" key={i}>

                                                                                <div style={{ width: '100px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                </div>
                                                                                <div style={{ width: '100px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        reportData?.keys?.map((month, index) => (

                                                                            <div className="flex align-items-center justify-between  mb-1" key={index}>

                                                                                <div className="text-gray-800" style={{ minWidth: '110px' }}>
                                                                                    {selectedFilterBy === "MONTH"
                                                                                        ? moment(month, "YYYY-MM").format("MMMM YYYY") 
                                                                                        : month}
                                                                                </div>
                                                                                <div>

                                                                                    {reportData?.revenue?.total_revenue[month]?.usd !== undefined
                                                                                        ? `$ ${reportData.revenue.total_revenue[month].usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                                                        : '<span class="text-gray-600">$ 0.00</span>'}
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    )
                                                                }
                                                            </div>
                                                        )
                                                    }
                                                        

                                                        



                                                    <div
                                                        className="order-list-card"
                                                        style={{
                                                            background: 'rgb(249, 249, 249)',
                                                            padding: '10px 0',
                                                            display: 'flex',
                                                            border: '0',
                                                            fontSize: '16px',
                                                        }}
                                                    >
                                                        Expenses
                                                    </div>    



                                                    <div
                                                        className="order-list-card"
                                                        style={{
                                                            background: '#fff',
                                                            // marginBottom: 10,
                                                            borderTopLeftRadius: '12px',
                                                            borderTopRightRadius: '12px',
                                                            borderBottomRightRadius: showPurchaseOrdersCard ? '0px' : '12px',
                                                            borderBottomLeftRadius: showPurchaseOrdersCard ? '0px' : '12px',
                                                            padding: '10px 20px',
                                                            display: 'flex',
                                                            flexDirection: 'initial',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            gap: 10, 
                                                            border: '1px solid #dfdfdf'
                                                        }}
                                                        onClick={() => {
                                                            if (!isLoading) {
                                                                setShowPurchaseOrdersCard(!showPurchaseOrdersCard)
                                                            }
                                                        }}
                                                    >
                                                        <div>
                                                            Purchase Orders
                                                        </div>
                                                        <div>
                                                            
                                                            <div
                                                                className={`invoice-attachment-collapse-icon ${showPurchaseOrdersCard ? 'collapsed' : ''}`}
                                                                style={{
                                                                    height: '17px', 
                                                                    fontSize: '25px', 
                                                                    background: '#fff'
                                                                }}
                                                            >
                                                                {/* {collapsedAttachments[index] ? <LuChevronDown /> : <LuChevronUp />} */}
                                                                <LuChevronDown />
                                                            </div>
                                                        </div>
                                                        
                                                    </div> 
                                                    {
                                                        showPurchaseOrdersCard && (
                                                            <div
                                                                className="order-list-card"
                                                                style={{
                                                                    background: '#fff',
                                                                    padding: 16,
                                                                    gap: 10, 
                                                                    border: '1px solid #dfdfdf',
                                                                    borderBottomLeftRadius: '12px',
                                                                    borderBottomRightRadius: '12px',
                                                                }}
                                                            >
                                                                {
                                                                    isLoading ? (
                                                                        [...Array(5)].map((__, i) => (
                                                                            <div className="flex align-items-center justify-between mb-1" key={i}>

                                                                                <div style={{ width: '100px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                </div>
                                                                                <div style={{ width: '100px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        reportData?.keys?.map((month, index) => (

                                                                            <div className="flex align-items-center justify-between  mb-1" key={index}>

                                                                                <div className="text-gray-800" style={{ minWidth: '110px' }}>
                                                                                    {selectedFilterBy === "MONTH"
                                                                                        ? moment(month, "YYYY-MM").format("MMMM YYYY") 
                                                                                        : month}
                                                                                </div>
                                                                                <div>

                                                                                    {reportData?.expenses?.purchase_orders[month]?.usd !== undefined
                                                                                        ? `$ ${reportData.expenses?.purchase_orders[month].usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                                                        : '<span class="text-gray-600">$ 0.00</span>'}
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    )
                                                                }
                                                            </div>
                                                        )
                                                    }    

                                                    <div
                                                        className="order-list-card"
                                                        style={{
                                                            background: '#fff',
                                                            marginTop: '15px',
                                                            borderTopLeftRadius: '12px',
                                                            borderTopRightRadius: '12px',
                                                            borderBottomRightRadius: showShippingCostsCard ? '0px' : '12px',
                                                            borderBottomLeftRadius: showShippingCostsCard ? '0px' : '12px',
                                                            padding: '10px 20px',
                                                            display: 'flex',
                                                            flexDirection: 'initial',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            gap: 10, 
                                                            border: '1px solid #dfdfdf'
                                                        }}
                                                        onClick={() => {
                                                            if (!isLoading) {
                                                                setShowShippingCostsCard(!showShippingCostsCard)
                                                            }
                                                        }}
                                                    >
                                                        <div>
                                                            Shipping Costs
                                                        </div>
                                                        <div>
                                                            
                                                            <div
                                                                className={`invoice-attachment-collapse-icon ${showShippingCostsCard ? 'collapsed' : ''}`}
                                                                style={{
                                                                    height: '17px', 
                                                                    fontSize: '25px', 
                                                                    background: '#fff'
                                                                }}
                                                            >
                                                                {/* {collapsedAttachments[index] ? <LuChevronDown /> : <LuChevronUp />} */}
                                                                <LuChevronDown />
                                                            </div>
                                                        </div>
                                                        
                                                    </div> 
                                                    {
                                                        showShippingCostsCard && (
                                                            <div
                                                                className="order-list-card"
                                                                style={{
                                                                    background: '#fff',
                                                                    padding: 16,
                                                                    gap: 10, 
                                                                    border: '1px solid #dfdfdf',
                                                                    borderBottomLeftRadius: '12px',
                                                                    borderBottomRightRadius: '12px',
                                                                }}
                                                            >
                                                                {
                                                                    isLoading ? (
                                                                        [...Array(5)].map((__, i) => (
                                                                            <div className="flex align-items-center justify-between mb-1" key={i}>

                                                                                <div style={{ width: '100px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                </div>
                                                                                <div style={{ width: '100px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        reportData?.keys?.map((month, index) => (

                                                                            <div className="flex align-items-center justify-between  mb-1" key={index}>

                                                                                <div className="text-gray-800" style={{ minWidth: '110px' }}>
                                                                                    {selectedFilterBy === "MONTH"
                                                                                        ? moment(month, "YYYY-MM").format("MMMM YYYY") 
                                                                                        : month}
                                                                                </div>
                                                                                <div>

                                                                                    {reportData?.expenses?.shipping_costs[month]?.usd !== undefined
                                                                                        ? `$ ${reportData.expenses?.shipping_costs[month].usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                                                        : '<span class="text-gray-600">$ 0.00</span>'}
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    )
                                                                }
                                                            </div>
                                                        )
                                                    }
                                                    
                                                    <div
                                                        className="order-list-card"
                                                        style={{
                                                            background: '#fff',
                                                            marginTop: '15px',
                                                            borderTopLeftRadius: '12px',
                                                            borderTopRightRadius: '12px',
                                                            borderBottomRightRadius: showTotalExpensesCard ? '0px' : '12px',
                                                            borderBottomLeftRadius: showTotalExpensesCard ? '0px' : '12px',
                                                            padding: '10px 20px',
                                                            display: 'flex',
                                                            flexDirection: 'initial',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            gap: 10, 
                                                            border: '1px solid #dfdfdf'
                                                        }}
                                                        onClick={() => {
                                                            if (!isLoading) {
                                                                setShowTotalExpensesCard(!showTotalExpensesCard)
                                                            }
                                                        }}
                                                    >
                                                        <div>
                                                            Total Expenses
                                                        </div>
                                                        <div>
                                                            
                                                            <div
                                                                className={`invoice-attachment-collapse-icon ${showTotalExpensesCard ? 'collapsed' : ''}`}
                                                                style={{
                                                                    height: '17px', 
                                                                    fontSize: '25px', 
                                                                    background: '#fff'
                                                                }}
                                                            >
                                                                {/* {collapsedAttachments[index] ? <LuChevronDown /> : <LuChevronUp />} */}
                                                                <LuChevronDown />
                                                            </div>
                                                        </div>
                                                        
                                                    </div> 
                                                    {
                                                        showTotalExpensesCard && (
                                                                
                                                            <div
                                                                className="order-list-card"
                                                                style={{
                                                                    background: '#fff',
                                                                    padding: 16,
                                                                    gap: 10, 
                                                                    border: '1px solid #dfdfdf',
                                                                    borderBottomLeftRadius: '12px',
                                                                    borderBottomRightRadius: '12px',
                                                                }}
                                                            >
                                                                {
                                                                    isLoading ? (
                                                                        [...Array(5)].map((__, i) => (
                                                                            <div className="flex align-items-center justify-between mb-1" key={i}>

                                                                                <div style={{ width: '100px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                </div>
                                                                                <div style={{ width: '100px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        reportData?.keys?.map((month, index) => (

                                                                            <div className="flex align-items-center justify-between  mb-1" key={index}>

                                                                                <div className="text-gray-800" style={{ minWidth: '110px' }}>
                                                                                    {selectedFilterBy === "MONTH"
                                                                                        ? moment(month, "YYYY-MM").format("MMMM YYYY") 
                                                                                        : month}
                                                                                </div>
                                                                                <div>

                                                                                    {reportData?.expenses?.total_expenses[month]?.usd !== undefined
                                                                                        ? `$ ${reportData.expenses?.total_expenses[month].usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                                                        : '<span class="text-gray-600">$ 0.00</span>'}
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    )
                                                                }
                                                            </div>
                                                        )
                                                    }


                                                    <div
                                                        className="order-list-card"
                                                        style={{
                                                            background: '#fff',
                                                            marginTop: '15px',
                                                            borderTopLeftRadius: '12px',
                                                            borderTopRightRadius: '12px',
                                                            borderBottomRightRadius: showNetProfitCard ? '0px' : '12px',
                                                            borderBottomLeftRadius: showNetProfitCard ? '0px' : '12px',
                                                            padding: '10px 20px',
                                                            display: 'flex',
                                                            flexDirection: 'initial',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            gap: 10, 
                                                            border: '1px solid #dfdfdf'
                                                        }}
                                                        onClick={() => {
                                                            if (!isLoading) {
                                                                setShowNetProfitCard(!showNetProfitCard)
                                                            }
                                                        }}
                                                    >
                                                        <div>
                                                            Net Profit
                                                        </div>
                                                        <div>
                                                            
                                                            <div
                                                                className={`invoice-attachment-collapse-icon ${showNetProfitCard ? 'collapsed' : ''}`}
                                                                style={{
                                                                    height: '17px', 
                                                                    fontSize: '25px', 
                                                                    background: '#fff'
                                                                }}
                                                            >
                                                                <LuChevronDown />
                                                            </div>
                                                        </div>
                                                        
                                                    </div> 
                                                    {
                                                        showNetProfitCard && (
                                                                
                                                            <div
                                                                className="order-list-card"
                                                                style={{
                                                                    background: '#fff',
                                                                    padding: 16,
                                                                    gap: 10, 
                                                                    border: '1px solid #dfdfdf',
                                                                    borderBottomLeftRadius: '12px',
                                                                    borderBottomRightRadius: '12px',
                                                                }}
                                                            >
                                                                {
                                                                    isLoading ? (
                                                                        [...Array(5)].map((__, i) => (
                                                                            <div className="flex align-items-center justify-between mb-1" key={i}>

                                                                                <div style={{ width: '100px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                </div>
                                                                                <div style={{ width: '100px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        reportData?.keys?.map((month, index) => (

                                                                            <div className="flex align-items-center justify-between  mb-1" key={index}>

                                                                                <div className="text-gray-800" style={{ minWidth: '110px' }}>
                                                                                    {selectedFilterBy === "MONTH"
                                                                                        ? moment(month, "YYYY-MM").format("MMMM YYYY") 
                                                                                        : month}
                                                                                </div>
                                                                                <div>

                                                                                    {reportData?.net_profit[month]?.usd !== undefined
                                                                                        ? `$ ${reportData.net_profit[month].usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                                                        : '<span class="text-gray-600">$ 0.00</span>'}
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    )
                                                                }
                                                            </div>
                                                        )
                                                    }



                                                </div>   

                                            </>
                                        )
                                    }
                                    {isLoading && (
                                        !isMobile && (

                                            <div className="inv-cards-list d-md-none" style={{ padding: '12px 0' }}>
                                                {[...Array(15)].map((_, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="order-list-card"
                                                        style={{
                                                            background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
                                                            marginBottom: 10,
                                                            borderRadius: 12,
                                                            padding: 16,
                                                            minHeight: 90,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 10
                                                        }}
                                                    >
                                                        {[...Array(3)].map((__, i) => (
                                                            <div key={i} style={{ width: i === 0 ? '60%' : i === 1 ? '40%' : '80%' }}>
                                                                <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </>
    );
};

export default index;
